import { Image, Platform, StyleSheet, View } from "react-native"
import { TouchButton, Text } from "pusatec-react-native"
import React from "react"
import { useTranslation } from "react-i18next"
import * as ImagePicker from "expo-image-picker"
import { SelectedItemImage, guessImageMimeType } from "../utils/itemImage"
import { DoorhubFileImage } from "./DoorhubFileImage"

const getFileName = (uri: string) => {
	const segments = uri.split("/")
	const lastSegment = segments[segments.length - 1]

	if (!lastSegment) {
		return `item-${Date.now()}.jpg`
	}

	return lastSegment.split("?")[0]
}

interface Props {
	value?: SelectedItemImage | null
	existingImageFileId?: string | null
	onChange: (value: SelectedItemImage | null) => void
}

export const ItemImagePicker = ({ value, existingImageFileId, onChange }: Props) => {
	const { t } = useTranslation()
	const fileInputRef = React.useRef<HTMLInputElement | null>(null)
	const [objectUrl, setObjectUrl] = React.useState<string | null>(null)

	const revokeObjectUrl = (uri?: string | null) => {
		if (Platform.OS === "web" && uri) {
			URL.revokeObjectURL(uri)
		}
	}

	const previewUri = value?.uri

	const clearSelection = () => {
		revokeObjectUrl(objectUrl)
		setObjectUrl(null)
		onChange(null)
	}

	const pickNativeImage = async () => {
		if (Platform.OS === "web") {
			fileInputRef.current?.click()
			return
		}

		const permission = await ImagePicker.requestMediaLibraryPermissionsAsync()
		if (!permission.granted) {
			return
		}

		const result = await ImagePicker.launchImageLibraryAsync({
			mediaTypes: ImagePicker.MediaTypeOptions.Images,
			allowsEditing: false,
			quality: 0.7,
		})

		if (result.canceled || !result.assets.length) {
			return
		}

		const asset = result.assets[0]
		const name = asset.fileName ?? getFileName(asset.uri)
		const type = asset.type?.startsWith("image/")
			? asset.type
			: guessImageMimeType(name)

		onChange({
			uri: asset.uri,
			name,
			type,
		})
	}

	const handleWebFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]

		if (!file) {
			return
		}

		revokeObjectUrl(objectUrl)

		const preview = URL.createObjectURL(file)
		setObjectUrl(preview)

		onChange({
			uri: preview,
			name: file.name,
			type: file.type || guessImageMimeType(file.name),
			file,
		})
	}

	React.useEffect(() => {
		return () => {
			revokeObjectUrl(objectUrl)
		}
	}, [objectUrl])

	React.useEffect(() => {
		if (!value && objectUrl) {
			revokeObjectUrl(objectUrl)
			setObjectUrl(null)
		}
	}, [value, objectUrl])

	return (
		<View>
			<Text type="strong" text={t("Item image")} style={{ marginBottom: 10 }} />
			{previewUri ? (
				<Image style={styles.preview} source={{ uri: previewUri }} />
			) : existingImageFileId ? (
				<DoorhubFileImage fileId={existingImageFileId} style={styles.preview} resizeMode="contain" />
			) : (
				<View style={styles.placeholder}>
					<Text text={t("No item image yet")} />
				</View>
			)}

			<View style={styles.buttonRow}>
				{Platform.OS === "web" && (
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						onChange={handleWebFileChange}
						style={{ display: "none" }}
					/>
				)}

				<View style={styles.buttonWrapper}>
					<TouchButton
						text={value ? t("Change item image") : t("Add item image")}
						onPress={pickNativeImage}
					/>
				</View>
				{value && (
					<View style={styles.buttonWrapper}>
						<TouchButton
							type="secondary"
							text={t("Remove item image")}
							onPress={clearSelection}
						/>
					</View>
				)}
			</View>
		</View>
	)
}

const styles = StyleSheet.create({
	placeholder: {
		borderWidth: 1,
		borderColor: "#ccc",
		padding: 40,
		borderRadius: 8,
		alignItems: "center",
		justifyContent: "center",
		marginBottom: 10,
	},
	preview: {
		width: "100%",
		height: 200,
		resizeMode: "contain",
		marginBottom: 10,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: "#ccc",
	},
	buttonRow: {
		flexDirection: "row",
		flexWrap: "wrap",
	},
	buttonWrapper: {
		marginRight: 10,
		marginBottom: 10,
	},
})
